/**
 * Ouvrir une conversation — geste unique du cockpit.
 *
 * Un seul chemin d'ouverture, quel que soit le point de départ (la grille
 * Conversations, ou le nom d'un fan dans la table des transactions d'une
 * créatrice). Ce qu'il porte, et qu'aucun appelant ne doit ré-implémenter :
 *
 *   - le VERROU de conversation (pré-check + pose avant ouverture, refus si un
 *     autre opérateur la tient) — un second chemin d'ouverture sans verrou
 *     laisserait deux chatteurs écrire au même fan ;
 *   - ⭐ la RÉSERVATION d'un autre chatteur (Kayla, 11-09-2026 : « quand c'est
 *     son shift, les gens ne peuvent pas y aller — sauf s'il n'est pas
 *     présent ») et 📋 sa FILE (Kayla, 12-09-2026 : « quand c'est dans la
 *     file de quelqu'un, un autre chatter ne doit pas pouvoir y aller ») :
 *     refus si un autre la tient en ce moment, avec son pseudo dans la
 *     bannière. La règle est `reservation()` ;
 *   - le heartbeat d'activité de shift ;
 *   - le focus mobile (un seul panneau visible à la fois) ;
 *   - la résolution du panneau Conversations d'accueil : l'onglet fan atterrit
 *     TOUJOURS dans le groupe lié au panneau Conversations existant, jamais
 *     dans un panneau parallèle (demande user 2026-08-09).
 *
 * Les superviseurs (ceo / manager / superadmin) entrent partout sans poser de
 * verrou — comportement inchangé.
 */
import { useCallback, useEffect, useRef } from "react";
import { useSetAtom, useStore } from "jotai";

import { layoutAtom, mobileActiveInstanceIdAtom } from "@/core/state";
import { isMobileSync } from "@/core/hooks";
import { addPanel, removePanel } from "@/shell/layout";
import { preloadPanel } from "@/shell/PanelRegistry";

import { conversationByIdAtom } from "./atoms";
import { lockNameFromError, plafondFromError } from "./lockLogic";
import { maFileAtom, reservation } from "./pileDuChatteur";
import { useNomsDesChatteurs } from "./useNomsDesChatteurs";
import { setConversationLockOpenStateAtom } from "./lockOpenState";
import {
  CONVERSATIONS_PANEL_ID,
  findConversationsHostId,
  ONGLETS_FAN_MAX_AU_POSTE,
  openFanInLinkedGroup,
} from "./openFanTab";
import { posteDeChatteurAtom, posteImposeAtom } from "./posteDeChatteur";
import { useConversationLocks } from "./useConversationLocks";
import { useShiftActivityHeartbeat } from "./useShiftActivityHeartbeat";

export interface OpenFanTabRequest {
  conversationId: string;
  /** Titre humain de l'onglet (nom du fan). */
  title: string;
  /**
   * Panneau Conversations d'accueil. La grille passe SON instanceId (deux
   * panneaux Conversations gardent chacun leur groupe d'onglets fan) ; un
   * appelant extérieur l'omet et on résout le panneau ouvert — en le créant
   * s'il n'y en a aucun, faute de quoi le clic ne ferait rien.
   */
  sourceConversationsId?: string | null;
}

export interface UseOpenFanTabOptions {
  /** Appelé quand l'ouverture est refusée : la conversation est tenue par un
   *  autre opérateur. Reçoit son nom (chaîne vide si inconnu). */
  onLockConflict?: (lockerName: string, plafond?: number | null) => void;
}

const EMPTY_SET: ReadonlySet<string> = new Set();

export function useOpenFanTab(
  options?: UseOpenFanTabOptions,
): (request: OpenFanTabRequest) => void {
  const setLayout = useSetAtom(layoutAtom);
  const setMobileActive = useSetAtom(mobileActiveInstanceIdAtom);
  const setLockOpenState = useSetAtom(setConversationLockOpenStateAtom);
  const bumpShiftActivity = useShiftActivityHeartbeat();

  const locks = useConversationLocks();
  const { isBypass: locksBypass, acquire: acquireLock, myWorkosUserId } = locks;
  // ⭐ Le pseudo de l'épingleur, pour la bannière de refus. Via ref, comme le
  // verrou : le callback reste stable.
  const nomsDesChatteurs = useNomsDesChatteurs();
  const nomsRef = useRef(nomsDesChatteurs);
  nomsRef.current = nomsDesChatteurs;
  const moiRef = useRef(myWorkosUserId);
  moiRef.current = myWorkosUserId;
  // 🎯 Cinq onglets au poste de chatteur (cf `ONGLETS_FAN_MAX_AU_POSTE`). Lu
  // sur le store au moment du clic, pas capturé : le callback reste stable.
  const store = useStore();

  // Lus via ref pour que le callback reste référentiellement STABLE : la grille
  // le passe à des cartes memoïsées, une nouvelle référence à chaque MAJ Convex
  // casserait le bailout `React.memo` de toute la liste.
  const lockViewForRef = useRef(locks.viewFor);
  lockViewForRef.current = locks.viewFor;
  const onLockConflictRef = useRef(options?.onLockConflict);
  onLockConflictRef.current = options?.onLockConflict;

  // Le chunk du panneau fan se charge à l'avance : un appelant extérieur (table
  // des transactions) n'a aucune raison de l'avoir déjà monté.
  useEffect(() => {
    preloadPanel("fan");
    preloadPanel("fan-chat");
  }, []);

  return useCallback(
    ({ conversationId, title, sourceConversationsId }: OpenFanTabRequest) => {
      const doOpen = (): string | null => {
        let openedInstanceId: string | null = null;
        setLayout((prev) => {
          // Le panneau Conversations d'accueil : celui demandé, sinon celui
          // ouvert, sinon on l'ouvre. Aucun onglet fan ne doit vivre hors d'un
          // groupe lié à un panneau Conversations.
          let layout = prev;
          let source = sourceConversationsId ?? findConversationsHostId(prev);
          if (!source) {
            const added = addPanel(layout, CONVERSATIONS_PANEL_ID, {
              kind: "active-group",
            });
            layout = added.layout;
            source = added.instanceId;
          }
          const res = openFanInLinkedGroup(layout, {
            conversationId,
            sourceConversationsId: source,
            title,
            // 🪶 Le plafond ne vaut qu'au poste IMPOSÉ : un rôle libre en
            // « Lignes » garde tous ses onglets (Kayla, 08-10-2026).
            limite: store.get(posteImposeAtom)
              ? {
                  max: ONGLETS_FAN_MAX_AU_POSTE,
                  nonLus: (id) => store.get(conversationByIdAtom(id))?.unread_count ?? 0,
                }
              : undefined,
            // 🗂️ Au poste, Francis est un onglet du panneau de droite
            // (17-09-2026) : une colonne d'avant est retirée.
            sansFrancis: store.get(posteDeChatteurAtom),
          });
          openedInstanceId = res.instanceId;
          return res.layout;
        });
        // Sur mobile, le layout n'affiche qu'un panel a la fois — l'ouverture
        // d'un nouveau tab Fan ne le focus PAS automatiquement (l'user reste sur
        // ConversationsPanel). On force le switch via mobileActiveInstanceIdAtom
        // pour que la conv selectionnee s'ouvre immediatement, comme attendu
        // par l'UX mobile "tap card -> open conv".
        if (isMobileSync() && openedInstanceId) {
          setMobileActive(openedInstanceId);
        }
        return openedInstanceId;
      };

      // Superviseurs (ceo/manager/superadmin) : ouverture directe, aucun verrou
      // posé — ils peuvent entrer partout, même sur une conv tenue par un chatter.
      if (locksBypass) {
        setLockOpenState({ conversationId, state: { status: "acquired" } });
        doOpen();
        return;
      }

      // Pré-check verrou (état Convex DÉJÀ synchronisé) : si la conv est tenue
      // par un AUTRE opérateur, on REFUSE d'ouvrir tout de suite — pas
      // d'ouverture « molle » (surtout sur mobile, où doOpen bascule l'écran sur
      // la conv puis la referme après 1,8 s : le chatter la voyait s'ouvrir).
      // La course résiduelle (verrou pas encore synchronisé localement) reste
      // couverte par `acquireLock` plus bas (rejet ALREADY_LOCKED autoritaire).
      const preLock = lockViewForRef.current(conversationId);
      if (preLock.blocked) {
        onLockConflictRef.current?.(preLock.lock?.lockedByName ?? "");
        return;
      }

      // ⭐📋 Réservée par un autre, ou dans SA file : même refus, même
      // bannière — c'est SA discussion tant qu'il la tient. Lu sur la carte
      // telle que la liste l'a servie (`epinglee_par` / `epingle_protegee` /
      // `tenue_par`), et sur MA pile, qui sait avant la carte ce qui vient de
      // me revenir ; une carte absente ou sans les champs ne ferme rien.
      const carte = store.get(conversationByIdAtom(conversationId));
      const r = carte
        ? reservation(carte, moiRef.current, false, EMPTY_SET, store.get(maFileAtom))
        : null;
      if (r?.fermee) {
        const qui = r.tenuePar ?? r.par;
        onLockConflictRef.current?.(
          (qui ? nomsRef.current.get(qui) : undefined) ?? "",
        );
        return;
      }

      // Rôle verrouillable : on POSE le verrou AVANT d'ouvrir (gate anti-course).
      // Si un autre opérateur tient déjà la conv → ALREADY_LOCKED → refus +
      // bannière, sans ouvrir l'onglet.
      setLockOpenState({ conversationId, state: { status: "pending" } });
      const openedInstanceId = doOpen();

      void (async () => {
        try {
          await acquireLock(conversationId);
          // Shift gate Lot B : ouvrir une conv = activité — bump le heartbeat.
          bumpShiftActivity();
          setLockOpenState({ conversationId, state: { status: "acquired" } });
        } catch (err) {
          const name = lockNameFromError(err);
          const nonce = Date.now();
          onLockConflictRef.current?.(name, plafondFromError(err));
          setLockOpenState({
            conversationId,
            state: { status: "conflict", lockerName: name, nonce },
          });
          if (isMobileSync() && openedInstanceId) {
            window.setTimeout(() => {
              setLayout((prev) => removePanel(prev, openedInstanceId));
              setLockOpenState({ conversationId, state: { status: "idle" } });
            }, 1800);
          }
        }
      })();
    },
    [
      store,
      setLayout,
      setMobileActive,
      setLockOpenState,
      locksBypass,
      acquireLock,
      bumpShiftActivity,
    ],
  );
}
